import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React from 'react'
import EscalationContactsBody from 'scenes/RecoveryAI/components/EscalationContactsBody'
import SettingsPaneChrome from 'scenes/RecoveryAI/components/SettingsPaneChrome'
import { successNotificationOverrides } from 'scenes/RecoveryAI/components/successNotificationOverrides'
import { useEscalationContactsController } from 'scenes/RecoveryAI/components/useEscalationContactsController'
import { useUnsavedChangesPrompt } from 'scenes/RecoveryAI/Settings/useUnsavedChangesPrompt'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('recovery_ai.settings.escalation_contacts')

// id linking the disabled Save button to its visually-hidden reason via
// aria-describedby — so the blocking reason is announced on keyboard focus
// (a `title` on the wrapping span is mouse-hover-only — AI-32 review / WCAG 4.1.3).
//
//   context: module-level rather than instance-namespaced BECAUSE Save lives on
//   the consumer, never in the shared body — so this pane is the only surface
//   that renders the cue, and the config-modal footer renders no second one.
//   That is an invariant, not an accident: give the modal shell a
//   disabled-reason cue and this must become instanceId-derived like the
//   picker ids.
const SAVE_DISABLED_REASON_ID = 'escalation-contacts-save-disabled-reason'

// Post-onboarding Settings pane for choosing WHO is notified on escalate
// (AI-32). Recipients are existing merchant users, never free-typed emails
// (AI-4/D2). All fetching, hydration, dirty tracking and the two-write save
// live in useEscalationContactsController, which the Activation modal shares;
// this file is the pane's chrome around it: the load / users-error branches,
// the banners, and Save. Admin-only: non-admins see it read-only.
const EscalationContactsPane = () => {
  const [css] = useStyletron()
  const contacts = useEscalationContactsController({ notifyMode: 'staged' })

  // Warn before leaving with pending edits (in-app nav + refresh/close), matching
  // the six sibling panes. Load-bearing on THIS pane specifically because L13
  // moved the notify-location-managers toggle from persist-on-click to staged: it
  // renders its new position optimistically, so without this a merchant flicks it,
  // navigates away, and nothing tells them it never persisted — and a
  // non-notification is invisible.
  //
  //   context: `&& !saveSuccess` is not decoration. notifyDraft is never cleared
  //   on a successful staged save (the pane waits for the server value to catch
  //   up), so plain isDirty prompts WHILE the green "saved" banner is on screen —
  //   and a lagging replica refetch re-arms it. Unlike ActiveLocationsPane, the
  //   combined isDirty is right here: both halves require a user action, so
  //   there is no defaultDirty-style false warning to avoid. No guardBypassRef —
  //   that exists for Save-and-test's sanctioned navigation, which this pane has
  //   no button for.
  const unsavedChangesPrompt = useUnsavedChangesPrompt(
    contacts.isDirty && !contacts.saveSuccess
  )

  if (contacts.isLoading) {
    return (
      <div role="status" aria-label={t('loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  // A failed users FETCH blocks the picker entirely (no options to choose), so
  // it surfaces a hard error/retry — distinct from the demoted contacts-404/403
  // notice below, where the picker still renders for review.
  if (contacts.isUsersError) {
    return (
      <div className={`p-l ${css({ textAlign: 'center' })}`} role="alert">
        <Text.BodyText3 color="cayenne50">{t('fetch_error')}</Text.BodyText3>
      </div>
    )
  }

  const saveDisabled =
    !contacts.canManage ||
    contacts.isSaving ||
    contacts.contactsUnavailable ||
    !contacts.isDirty

  // The reason Save is disabled, for the aria-describedby cue below (early
  // returns avoid a nested ternary). Deliberately silent on the pristine case:
  // a Save with nothing to persist needs no explanation, matching
  // ActiveLocationsPane's all-mode behaviour — and needs no new locale key.
  const resolveSaveDisabledReason = (): string | undefined => {
    if (!contacts.canManage) return t('save_disabled_no_permission')
    if (contacts.contactsUnavailable) return t('save_disabled_tooltip')
    if (contacts.isSaving) return t('save_disabled_saving')
    return undefined
  }
  const saveDisabledReason = resolveSaveDisabledReason()

  return (
    <SettingsPaneChrome
      title={t('title')}
      subtitle={t('subtitle')}
      // Ships full-bleed today, and its subtitle→rationale gap is the 32px
      // Figma rhythm rather than the 48px the config panes use.
      constrainWidth={false}
      contentGap="l"
    >
      {unsavedChangesPrompt}
      {contacts.contactsUnavailable && (
        <Notification kind="warning" size="full">
          {/* Two states, two strings: with a retained set there is something to
              review; with none the pickers are empty and disabled, and the
              review copy would read as "you have no recipients". */}
          {contacts.recipientsReadOnly
            ? t('unloadable_notice')
            : t('unavailable_notice')}
        </Notification>
      )}
      {/* #3883: the roles lookup is what tells ADMINS from ALL OTHER USERS. When it
          can't resolve — no `members: view`, or the request failed — every RBAC
          admin demotes into ALL OTHER USERS with no badge. Say so rather than
          render a confidently-wrong grouping; non-blocking, the pickers still work. */}
      {contacts.adminGroupingUnavailable && (
        <Notification kind="info" size="full">
          {t('admin_grouping_unavailable')}
        </Notification>
      )}
      {/* The two negative banners are mutually exclusive: a notify-only failure
          means the recipients DID land, so stacking the generic "failed to save
          your escalation contacts" under it would contradict itself. */}
      {contacts.isSaveError && !contacts.isNotifyOnlySaveError && (
        <Notification kind="negative" size="full">
          {t('save_error')}
        </Notification>
      )}
      {contacts.isNotifyOnlySaveError && (
        <Notification kind="negative" size="full">
          {t('notify_save_error')}
        </Notification>
      )}
      {contacts.saveSuccess && !contacts.isSaveError && (
        <Notification
          kind="positive"
          size="full"
          overrides={successNotificationOverrides}
        >
          {t('save_success')}
        </Notification>
      )}

      <EscalationContactsBody
        instanceId="settings"
        users={contacts.users}
        primaryIds={contacts.primaryIds}
        onPrimaryChange={contacts.onPrimaryChange}
        additionalIds={contacts.additionalIds}
        onAdditionalChange={contacts.onAdditionalChange}
        notifyLocationManagers={contacts.notifyLocationManagers}
        onNotifyChange={contacts.onNotifyChange}
        canManage={contacts.canManage}
        isAdmin={contacts.isUserAdmin}
        isNotifySaving={contacts.isNotifySaving}
        isBusy={contacts.isBusy}
        recipientsReadOnly={contacts.recipientsReadOnly}
      />

      <div className="mt-xl">
        {/* The visually-hidden span is the keyboard/screen-reader-accessible
            reason Save is disabled, wired to the Button via aria-describedby
            (WCAG 4.1.3); the span `title` mirrors it for sighted mouse hover
            (the uikit Button has no `title` prop). */}
        {saveDisabledReason && (
          <span id={SAVE_DISABLED_REASON_ID} className="sr-only">
            {saveDisabledReason}
          </span>
        )}
        <span title={saveDisabledReason}>
          <Button
            kind="primary"
            onClick={contacts.commit}
            disabled={saveDisabled}
            isLoading={contacts.isSaving}
            // Keep a stable accessible name: isLoading swaps the label for a
            // spinner, which would otherwise leave the button unnamed (WCAG
            // 4.1.2) while saving.
            aria-label={t('save')}
            aria-describedby={
              saveDisabledReason ? SAVE_DISABLED_REASON_ID : undefined
            }
          >
            {t('save')}
          </Button>
        </span>
      </div>
    </SettingsPaneChrome>
  )
}

export default EscalationContactsPane
